import React, { useState } from "react";
import { Typography, Row, Space, Button, Card, Tag, theme as antdTheme } from "antd";
import { ArrowRightOutlined, TeamOutlined, BankOutlined, SafetyCertificateOutlined, TrophyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useCustom } from '@refinedev/core';
import { RevenueAnalytics } from '../../components/sections/RevenueAnalytics';
import { DeviceAnalytics } from '../../components/sections/DeviceAnalytics';
import { DownloadsAnalytics } from '../../components/sections/DownloadsAnalytics';
import { DateFilterSelect, DateFilterType } from '../../components/DateFilterSelect';
import dayjs from 'dayjs';
import './dashboard.css';
import { TOKEN_KEY } from '../../authProvider';

type AdminOverview = {
  accounts: number;
  activeClubs: number;
  openReports: number | null;
  openReportsHasMore: boolean;
  upcomingScoutingEvents: number;
};

// Helper to create default date range (last month)
const getDefaultDateRange = () => {
  const now = dayjs();
  const monthAgo = now.subtract(1, 'month');
  return {
    from: monthAgo.startOf('day').toISOString(),
    to: now.endOf('day').toISOString()
  };
};

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { token: themeToken } = antdTheme.useToken();
  const { data: overviewResponse, isLoading: overviewLoading } = useCustom<AdminOverview>({
    url: `${import.meta.env.VITE_API_URL}/admin/dashboard/overview`,
    method: 'get',
    config: { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } },
  });
  const overview = overviewResponse?.data;
  // Master date filter state for all analytics
  const [dateFilter, setDateFilter] = useState<DateFilterType>('monthly');
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>(getDefaultDateRange);

  // Master date filter handler
  const handleDateChange = (filterType: DateFilterType, dates?: { from: string; to: string }) => {
    setDateFilter(filterType);
    if (dates) {
      setDateRange(dates);
    }
  };

  return (
    <div
      className="admin-dashboard"
      style={{
        '--dashboard-surface': themeToken.colorBgContainer,
        '--dashboard-border': themeToken.colorBorderSecondary,
        '--dashboard-text': themeToken.colorText,
      } as React.CSSProperties}
    >
      <header className="admin-dashboard__header">
        <div>
          <Typography.Text className="admin-dashboard__eyebrow">Platform command centre</Typography.Text>
          <Typography.Title level={2} style={{ margin: '4px 0 4px' }}>
            What needs your attention?
          </Typography.Title>
          <Typography.Text type="secondary">
            Live account, club, revenue, and wearable signals for the selected period.
          </Typography.Text>
        </div>
        <DateFilterSelect
          value={dateFilter}
          onChange={handleDateChange}
        />
      </header>

      <section aria-labelledby="quick-actions-title">
        <div className="admin-dashboard__section-heading">
          <div>
            <Typography.Title id="quick-actions-title" level={4}>Work queues</Typography.Title>
            <Typography.Text type="secondary">Move directly from the dashboard to day-to-day operations.</Typography.Text>
          </div>
          <Tag color="red">Live admin tools</Tag>
        </div>
        <div className="admin-dashboard__actions">
          <QuickAction icon={<TeamOutlined />} label="Manage users" detail={overviewLoading ? 'Loading accounts…' : `${overview?.accounts ?? 0} platform accounts`} onClick={() => navigate('/users')} />
          <QuickAction icon={<BankOutlined />} label="Manage clubs" detail={overviewLoading ? 'Loading clubs…' : `${overview?.activeClubs ?? 0} active clubs`} onClick={() => navigate('/clubs')} />
          <QuickAction
            urgent={(overview?.openReports ?? 0) > 0}
            icon={<SafetyCertificateOutlined />}
            label="Review reports"
            detail={overviewLoading
              ? 'Loading reports…'
              : overview?.openReports == null
                ? 'Open moderation queue'
                : `${overview.openReports}${overview.openReportsHasMore ? '+' : ''} need review`}
            onClick={() => navigate('/reports')}
          />
          <QuickAction icon={<TrophyOutlined />} label="Scouting events" detail={overviewLoading ? 'Loading events…' : `${overview?.upcomingScoutingEvents ?? 0} upcoming events`} onClick={() => navigate('/scouting-events')} />
        </div>
      </section>

      <section aria-labelledby="growth-title">
        <div className="admin-dashboard__section-heading">
          <div>
            <Typography.Title id="growth-title" level={4}>Account growth</Typography.Title>
            <Typography.Text type="secondary">Who joined GymLive, and where they are coming from.</Typography.Text>
          </div>
        </div>
        <Row gutter={[20, 20]}>
        <DownloadsAnalytics dateRange={dateRange} />
        </Row>
      </section>

      <section aria-labelledby="revenue-title">
        <div className="admin-dashboard__section-heading">
          <div>
            <Typography.Title id="revenue-title" level={4}>Commercial health</Typography.Title>
            <Typography.Text type="secondary">Reconciled club money movement and GymLive commission.</Typography.Text>
          </div>
        </div>
        <Row gutter={[20, 20]}>
        <RevenueAnalytics dateRange={dateRange} />
        </Row>
      </section>

      <section aria-labelledby="devices-title">
        <div className="admin-dashboard__section-heading">
          <div>
            <Typography.Title id="devices-title" level={4}>Wearable coverage</Typography.Title>
            <Typography.Text type="secondary">Connected provider footprint across the platform.</Typography.Text>
          </div>
        </div>
        <Row gutter={[20, 20]}>
        <DeviceAnalytics dateRange={dateRange} />
        </Row>
      </section>
    </div>
  );
};

function QuickAction({ icon, label, detail, urgent = false, onClick }: { icon: React.ReactNode; label: string; detail: string; urgent?: boolean; onClick: () => void }) {
  return (
    <Card className={`admin-dashboard__action${urgent ? ' admin-dashboard__action--urgent' : ''}`} hoverable onClick={onClick}>
      <div className="admin-dashboard__action-icon">{icon}</div>
      <Space direction="vertical" size={0} style={{ minWidth: 0 }}>
        <Typography.Text strong>{label}</Typography.Text>
        <Typography.Text type={urgent ? 'danger' : 'secondary'} ellipsis>{detail}</Typography.Text>
      </Space>
      <Button type="text" aria-label={`Open ${label}`} icon={<ArrowRightOutlined />} onClick={(event) => { event.stopPropagation(); onClick(); }} />
    </Card>
  );
}
